// ==============================================================================
// DEV-195: TENANT HEALTH MONITORING
// ==============================================================================

/**
 * Overall health status
 */
export type HealthStatus = 'healthy' | 'degraded' | 'critical' | 'unknown';

/**
 * Service status
 */
export type ServiceStatus = 'operational' | 'degraded' | 'outage' | 'maintenance' | 'unknown';

/**
 * Health check type
 */
export type HealthCheckType =
  | 'database'
  | 'cache'
  | 'api'
  | 'websocket'
  | 'storage'
  | 'compute'
  | 'network'
  | 'external'
  | 'queue'
  | 'scheduler';

/**
 * Metric severity level
 */
export type MetricSeverity = 'normal' | 'warning' | 'critical';

/**
 * Health check result
 */
export interface HealthCheckResult {
  checkType: HealthCheckType;
  name: string;
  status: ServiceStatus;
  message: string;
  responseTimeMs: number;
  lastChecked: string;
  details?: Record<string, unknown>;
}

/**
 * System metric
 */
export interface SystemMetric {
  name: string;
  value: number;
  unit: string;
  threshold?: {
    warning: number;
    critical: number;
  };
  severity: MetricSeverity;
  trend?: 'up' | 'down' | 'stable';
  history?: MetricDataPoint[];
}

/**
 * Metric data point for time series
 */
export interface MetricDataPoint {
  timestamp: string;
  value: number;
}

/**
 * Service health status
 */
export interface ServiceHealth {
  serviceId: string;
  serviceName: string | null;
  serviceType: string;
  status: ServiceStatus;
  uptime: number | null; // percentage, only when supplied by a measured contract
  responseTimeMs: number | null;
  lastChecked?: string | null;
  message?: string | null;
  lastIncident?: string;
  checks: HealthCheckResult[] | null;
}

/**
 * Tenant health summary
 */
export interface TenantHealthSummary {
  tenantId: string;
  overallStatus: HealthStatus;
  score: number | null; // 0-100, or unavailable
  lastUpdated: string;
  services: ServiceHealth[];
  metrics: SystemMetric[];
  activeIncidents: HealthIncident[];
  recentEvents: HealthEvent[];
}

/**
 * Health incident
 */
export interface HealthIncident {
  id: string;
  tenantId: string;
  title: string;
  description: string | null;
  /** minor/major remain legacy event values; inventory uses low/medium/high/critical. */
  severity: 'low' | 'medium' | 'high' | 'critical' | 'minor' | 'major';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  affectedServices: string[] | null;
  startedAt: string;
  resolvedAt?: string;
  acknowledgedAt?: string | null;
  resolution?: string | null;
  updates: IncidentUpdate[] | null;
}

/**
 * Incident update
 */
export interface IncidentUpdate {
  id: string;
  message: string;
  status: HealthIncident['status'];
  timestamp: string;
  author?: string;
}

/**
 * Health event
 */
export interface HealthEvent {
  id: string;
  tenantId: string;
  type: 'incident' | 'recovery' | 'maintenance' | 'alert' | 'deployment' | 'config_change';
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'error';
  timestamp: string;
  source: string;
  metadata?: Record<string, unknown>;
}

/**
 * Health alert configuration
 */
export interface HealthAlertConfig {
  id: string;
  tenantId: string;
  name: string;
  enabled: boolean;
  conditions: AlertCondition[];
  actions: AlertAction[];
  cooldownMinutes: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Alert condition
 */
export interface AlertCondition {
  metric: string;
  operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte' | 'ne';
  threshold: number;
  duration?: number; // seconds
}

/**
 * Alert action
 */
export interface AlertAction {
  type: 'email' | 'webhook' | 'slack' | 'pagerduty' | 'sms';
  target: string;
  config?: Record<string, unknown>;
}

/**
 * Uptime stats
 */
export interface UptimeStats {
  tenantId: string;
  period: 'day' | 'week' | 'month' | 'year';
  overallUptime: number; // percentage
  uptimeByService: Record<string, number>;
  incidentCount: number;
  mttr: number; // mean time to recovery in minutes
  mttd: number; // mean time to detect in minutes
}

/**
 * Real-time health update (WebSocket message)
 */
export interface HealthUpdate {
  type: 'status_change' | 'metric_update' | 'incident_update' | 'alert_triggered';
  tenantId: string;
  timestamp: string;
  payload: HealthStatusChange | MetricUpdate | IncidentUpdatePayload | AlertTriggered;
}

/**
 * Health status change payload
 */
export interface HealthStatusChange {
  previousStatus: HealthStatus;
  currentStatus: HealthStatus;
  reason: string;
  affectedServices: string[];
}

/**
 * Metric update payload
 */
export interface MetricUpdate {
  metrics: SystemMetric[];
}

/**
 * Incident update payload
 */
export interface IncidentUpdatePayload {
  incident: HealthIncident;
  updateType: 'created' | 'updated' | 'resolved';
}

/**
 * Alert triggered payload
 */
export interface AlertTriggered {
  alertId: string;
  alertName: string;
  condition: AlertCondition;
  currentValue: number;
  timestamp: string;
}

/**
 * Health status info metadata
 */
export interface HealthStatusInfo {
  status: HealthStatus;
  label: string;
  description: string;
  color: string;
  icon: string;
}

/**
 * Health status metadata lookup
 */
export const HEALTH_STATUS_INFO: Record<HealthStatus, HealthStatusInfo> = {
  healthy: {
    status: 'healthy',
    label: 'Healthy',
    description: 'All systems operational',
    color: 'success',
    icon: 'check-circle'
  },
  degraded: {
    status: 'degraded',
    label: 'Degraded',
    description: 'Some systems experiencing issues',
    color: 'warning',
    icon: 'alert-triangle'
  },
  critical: {
    status: 'critical',
    label: 'Critical',
    description: 'Major systems unavailable',
    color: 'danger',
    icon: 'alert-circle'
  },
  unknown: {
    status: 'unknown',
    label: 'Unknown',
    description: 'Unable to determine status',
    color: 'secondary',
    icon: 'help-circle'
  }
};

/**
 * Service status info metadata
 */
export interface ServiceStatusInfo {
  status: ServiceStatus;
  label: string;
  color: string;
}

/**
 * Service status metadata lookup
 */
export const SERVICE_STATUS_INFO: Record<ServiceStatus, ServiceStatusInfo> = {
  operational: { status: 'operational', label: 'Operational', color: 'success' },
  degraded: { status: 'degraded', label: 'Degraded', color: 'warning' },
  outage: { status: 'outage', label: 'Outage', color: 'danger' },
  maintenance: { status: 'maintenance', label: 'Maintenance', color: 'info' },
  unknown: { status: 'unknown', label: 'Unknown', color: 'secondary' }
};

/**
 * Health check type info metadata
 */
export interface HealthCheckTypeInfo {
  type: HealthCheckType;
  label: string;
  description: string;
  icon: string;
}

/**
 * Health check type metadata lookup
 */
export const HEALTH_CHECK_TYPE_INFO: Record<HealthCheckType, HealthCheckTypeInfo> = {
  database: { type: 'database', label: 'Database', description: 'Database connectivity', icon: 'database' },
  cache: { type: 'cache', label: 'Cache', description: 'Cache service', icon: 'layers' },
  api: { type: 'api', label: 'API', description: 'API endpoints', icon: 'server' },
  websocket: { type: 'websocket', label: 'WebSocket', description: 'Real-time connections', icon: 'radio' },
  storage: { type: 'storage', label: 'Storage', description: 'File storage', icon: 'hard-drive' },
  compute: { type: 'compute', label: 'Compute', description: 'Processing capacity', icon: 'cpu' },
  network: { type: 'network', label: 'Network', description: 'Network connectivity', icon: 'globe' },
  external: { type: 'external', label: 'External', description: 'External services', icon: 'external-link' },
  queue: { type: 'queue', label: 'Queue', description: 'Message queue', icon: 'list' },
  scheduler: { type: 'scheduler', label: 'Scheduler', description: 'Task scheduler', icon: 'clock' }
};

/**
 * Calculate overall health status from services
 */
export function calculateOverallHealth(services: ServiceHealth[]): HealthStatus {
  if (services.length === 0) {
    return 'unknown';
  }

  const hasOutage = services.some(s => s.status === 'outage');
  if (hasOutage) {
    return 'critical';
  }

  const hasDegraded = services.some(s => s.status === 'degraded');
  if (hasDegraded) {
    return 'degraded';
  }

  const hasUnknown = services.some(s => s.status === 'unknown');
  if (hasUnknown) {
    return 'unknown';
  }

  return 'healthy';
}

/**
 * Calculate health score from metrics
 */
export function calculateHealthScore(metrics: SystemMetric[]): number | null {
  if (metrics.length === 0) {
    return null;
  }

  let totalScore = 0;
  let weightSum = 0;

  for (const metric of metrics) {
    const weight = metric.severity === 'critical' ? 3 : metric.severity === 'warning' ? 2 : 1;
    weightSum += weight;

    if (metric.severity === 'normal') {
      totalScore += weight * 100;
    } else if (metric.severity === 'warning') {
      totalScore += weight * 60;
    } else {
      totalScore += weight * 20;
    }
  }

  return weightSum > 0 ? Math.round(totalScore / weightSum) : 100;
}

/**
 * Format uptime percentage.
 *
 * Returns "—" for non-finite input rather than leaking "NaN%" into the UI.
 * Clamps to the [0, 100] range: a metric source returning -1 or 101 should
 * render as 0% or 100% rather than an impossible value on an SLA dashboard.
 * Pure formatter — callers that need to telemeter anomalies should do so at
 * the call site with their own logger.
 */
export function formatUptime(uptime: number): string {
  if (!Number.isFinite(uptime)) {
    return '—';
  }
  const clamped = Math.min(100, Math.max(0, uptime));
  return `${clamped.toFixed(3)}%`;
}

/**
 * Format response time
 */
export function formatResponseTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) { return 'Unavailable'; }
  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Get severity from metric value and thresholds
 */
export function getMetricSeverity(
  value: number,
  warningThreshold: number,
  criticalThreshold: number,
  isHigherWorse = true
): MetricSeverity {
  if (isHigherWorse) {
    if (value >= criticalThreshold) {
      return 'critical';
    }
    if (value >= warningThreshold) {
      return 'warning';
    }
    return 'normal';
  } else {
    if (value <= criticalThreshold) {
      return 'critical';
    }
    if (value <= warningThreshold) {
      return 'warning';
    }
    return 'normal';
  }
}
